// src/utils/window/windowUtils.ts

import type { SingleWindow } from '../models/single-window.model';

/**
 * Get typed reference to the global window object
 * with Single platform custom properties
 */
export function getWindow(): SingleWindow {
  if (typeof window === 'undefined') {
    throw new Error('window is not defined (SSR environment?)');
  }
  return window as SingleWindow;
}

/**
 * Get URL search parameters from current location
 */
export function getSearchParams(): URLSearchParams {
  return new URL(window.location.href).searchParams;
}

/**
 * Get current path (pathname + search)
 */
export function getPath(): string {
  const url = new URL(window.location.href);
  return url.pathname + url.search;
}

/**
 * Get current origin
 */
export function getOrigin(): string {
  return window.location.origin;
}

/**
 * Get current hostname
 */
export function getHostname(): string {
  return window.location.hostname;
}

/**
 * Get current pathname
 */
export function getPathname(): string {
  return window.location.pathname;
}

/**
 * Get current href
 */
export function getHref(): string {
  return window.location.href;
}

/**
 * Get current hash
 */
export function getHash(): string {
  return window.location.hash;
}

/**
 * Get URL without hash fragment
 */
export function getHashlessUrl(): string {
  const url = new URL(window.location.href);
  return url.origin + url.pathname + url.search;
}

/**
 * Get current URL, with special handling for hash-based routes
 * Removes hash for /join and /my-account routes
 */
export function getCurrentUrl(): string {
  const hash = getHash();
  if (hash && (hash.includes('/join') || hash.includes('/my-account'))) {
    return getHashlessUrl();
  }
  return getHref();
}

/**
 * Navigate to a URL
 * @param url - URL to navigate to
 * @param newTab - Open in new tab (default: false)
 */
export function windowNavigate(url: string | undefined, newTab: boolean = false): void {
  if (!url) {
    return;
  }

  // If URL is same as current, reload
  if (url === window.location.href) {
    window.location.reload();
    return;
  }

  // Open in new tab
  if (newTab) {
    openTab(url);
    return;
  }

  // Navigate to URL
  window.location.href = url;
}

/**
 * Open URL in new tab
 * @param url - URL to open
 */
export function openTab(url: string): void {
  window.open(url, '_blank');
}

/**
 * Check if running in Shopify theme editor
 */
// export function isShopifyThemeEditor(): boolean {
//     const win = getWindow();
//     return !!win.Shopify?.designMode;
// }

/**
 * Check if custom selectors are available
 */
export function hasCustomSelectors(): boolean {
  const win = getWindow();
  return !!(win.SINGLE_CUSTOM_CART_BUTTON_SELECTORS || win.SINGLE_CUSTOM_CHECKOUT_BUTTON_SELECTORS);
}

/**
 * Get Shopify money format
 * Default: '${{amount}}'
 */
export function getMoneyFormat(): string {
  const win = getWindow();
  return win.theme?.moneyFormat ?? '${{amount}}';
}
